import type { AxiosRequestConfig } from "axios";
import api from "../../../utils/api";
import { unwrap } from "./apiEnvelope";
import type { InsightsSnapshot } from "../../comportamiento/types";

// ============================================================================
// CRM — cuenta de demostración de un negocio (épica #1318) — servicio.
//
// Un negocio que llega a «Demo agendada» recibe una cuenta de demo en la
// plataforma del producto (WAYAK). La cuenta VIVE ALLÁ: en el negocio sólo hay
// un puntero (`deal.demo`) y todo lo demás —estado, accesos, datos de ejemplo—
// se lee en vivo cada vez que se abre la ficha. Por eso este servicio no guarda
// nada en un store: cada apertura pregunta.
//
// Habla con torre-de-control-api → módulo crmDeals:
//   • estado      GET    /crm/deals/:id/demo
//   • crear       POST   /crm/deals/:id/demo            { email? }
//   • reinvitar   POST   /crm/deals/:id/demo/invite     (regenera el enlace)
//   • convertir   POST   /crm/deals/:id/demo/convert    (borra los datos de ejemplo)
//   • cerrar      POST   /crm/deals/:id/demo/close
//   • reabrir     POST   /crm/deals/:id/demo/reopen
//   • resembrar   POST   /crm/deals/:id/demo/reseed     (reintenta los datos de ejemplo)
//   • borrar      DELETE /crm/deals/:id/demo
//   • cancelar    DELETE /crm/deals/:id/demo/pending    (el cierre/conversión programado)
//   • uso         GET    /crm/deals/:id/demo/behavior?days=
//   • ajustes     GET/PUT /crm/demo-config
//
// Todo lo que escribe (y el uso, que es información de una persona) es sólo de
// administrador de plataforma; la API lo exige, la pantalla sólo esconde botones.
// A quien no lo es, la API le manda una versión RECORTADA de la demo (estado y
// accesos, sin datos del prospecto ni de la otra plataforma): por eso casi
// todos los campos de estos tipos son opcionales.
//
// El automatismo no cierra ni convierte en caliente: deja la acción PENDIENTE
// y la ejecuta cuando la etapa lleva unos minutos sin cambiar. Mientras tanto
// se ve («Se cerrará en ~N min») y un administrador puede cancelarla.
// ============================================================================

/** Estado del automatismo guardado en el negocio (lo único que CC-CONSOLE conserva). */
export type DemoPointerState = "requested" | "ready" | "needs_email" | "error" | "converted" | "closed" | "purged";

/** Cierre o conversión que el automatismo ejecutará si la etapa no cambia. */
export interface DemoPending {
  action: "close" | "convert";
  since: string | null;
  /** Cuándo está previsto (aproximado: la revisión corre cada 5 minutos). */
  dueAt?: string | null;
}

export interface DealDemoPointer {
  state: DemoPointerState;
  pending?: DemoPending | null;
  // --- sólo para administradores de plataforma ---
  tenant?: string;
  accountId?: string | null;
  requestedAt?: string | null;
  updatedAt?: string | null;
  lastAction?: string | null;
  error?: { code: string; message: string; at: string } | null;
  /** Acción que un administrador canceló y queda retenida mientras la etapa no cambie. */
  hold?: "close" | "convert" | null;
  // --- versión recortada ---
  /** La última operación falló (sin el detalle). */
  hasError?: boolean;
}

export type DemoAccountStatus = "invited" | "active" | "converted" | "closed" | "purged";

/** La cuenta tal como la devuelve el tenant. Se lee en vivo; nunca se guarda aquí. */
export interface DemoAccount {
  id?: string;
  dealId?: string;
  status: DemoAccountStatus;
  /** Versión recortada: sólo si alguien ya entró, sin decir quién. */
  hasUser?: boolean;
  /** Hasta cuándo sirve el enlace de invitación. */
  inviteExpiresAt?: string | null;
  email?: string | null;
  contactName?: string | null;
  companyName?: string | null;
  companyId?: string | null;
  userId?: string | null;
  seed?: { status?: string; counts?: Record<string, number>; error?: string | null } | null;
  firstLoginAt?: string | null;
  lastLoginAt?: string | null;
  loginCount?: number | null;
  graceDays?: number | null;
  closedAt?: string | null;
  purgeAfter?: string | null;
  convertedAt?: string | null;
  purgedAt?: string | null;
  createdAt?: string | null;
}

export interface DealDemoView {
  demo: DealDemoPointer | null;
  account: DemoAccount | null;
  /** Código del fallo al leer la cuenta en vivo (la plataforma no respondió). */
  accountError?: string | null;
  /** `/comportamiento?t=<tenant>&u=<userId>` cuando alguien ya entró. */
  behaviorLink: string | null;
  /** La API mandó la versión recortada (quien mira no es administrador de plataforma). */
  restricted?: boolean;
  /** Lo que la pantalla necesita de la configuración para avisar bien. */
  settings?: { enabled: boolean; graceDays: number; settleMinutes: number };
}

export interface DealDemoActionResult extends DealDemoView {
  /** Sólo al crear y al reinvitar. Lleva el token: se muestra/copia, no se guarda. */
  inviteUrl: string | null;
}

export type DemoAction = "invite" | "convert" | "close" | "reopen" | "reseed";

export interface DemoConfig {
  enabled: boolean;
  tenant: string;
  stageLabels: string[];
  localStages: string[];
  productMatch: string;
  graceDays: number;
  /** Minutos que la etapa debe sostenerse antes de cerrar o convertir. */
  settleMinutes: number;
  maxProvisionsPerHour: number;
  maxDestructivePerHour: number;
}

export interface DemoConfigView {
  config: DemoConfig;
  /** Plataformas donde se puede crear una demo. */
  tenants: { id: string; name: string }[];
  /** Etapas locales que se pueden marcar como «Demo agendada». */
  localStages: string[];
}

/**
 * La ficha de uso con los campos de «tiempo por página» como opcionales: el
 * módulo Comportamiento los está agregando y esta pantalla no debe romperse
 * con una API que todavía no los manda.
 */
export type DemoBehaviorSnapshot = Omit<InsightsSnapshot, "topPages"> & {
  topPages: (InsightsSnapshot["topPages"][number] & { avgSeconds?: number | null; totalSeconds?: number | null })[];
};

export type DealDemoBehavior =
  | { hasUser: false }
  | { hasUser: true; snapshot: DemoBehaviorSnapshot; behaviorLink: string | null };

// Los negocios son globales y la demo vive en otra plataforma: no depende de la
// compañía que el usuario tenga seleccionada.
const scoped = (signal?: AbortSignal, params?: Record<string, string | number>) =>
  ({ signal, params, _skipCompanyScope: true }) as AxiosRequestConfig;

const base = (dealId: string) => `/crm/deals/${encodeURIComponent(dealId)}/demo`;

export async function fetchDealDemo(dealId: string, signal?: AbortSignal): Promise<DealDemoView> {
  return unwrap<DealDemoView>(await api.get(base(dealId), scoped(signal)));
}

/** «Crear demo». `email` sólo hace falta cuando el negocio no tiene contacto con correo. */
export async function createDealDemo(dealId: string, email?: string): Promise<DealDemoActionResult> {
  return unwrap<DealDemoActionResult>(await api.post(base(dealId), email ? { email } : {}, scoped()));
}

export async function runDealDemoAction(dealId: string, action: DemoAction): Promise<DealDemoActionResult> {
  return unwrap<DealDemoActionResult>(await api.post(`${base(dealId)}/${action}`, {}, scoped()));
}

/** «Borrar ahora»: elimina la compañía de demo y sus datos en la plataforma. */
export async function deleteDealDemo(dealId: string): Promise<DealDemoActionResult> {
  return unwrap<DealDemoActionResult>(await api.delete(base(dealId), scoped()));
}

/** Cancela el cierre o la conversión que el automatismo dejó programado. */
export async function cancelDealDemoPending(dealId: string): Promise<DealDemoActionResult> {
  return unwrap<DealDemoActionResult>(await api.delete(`${base(dealId)}/pending`, scoped()));
}

export async function fetchDealDemoBehavior(dealId: string, days = 30, signal?: AbortSignal): Promise<DealDemoBehavior> {
  return unwrap<DealDemoBehavior>(await api.get(`${base(dealId)}/behavior`, scoped(signal, { days })));
}

export async function fetchDemoConfig(signal?: AbortSignal): Promise<DemoConfigView> {
  return unwrap<DemoConfigView>(await api.get("/crm/demo-config", scoped(signal)));
}

export async function saveDemoConfig(patch: Partial<DemoConfig>): Promise<DemoConfigView> {
  return unwrap<DemoConfigView>(await api.put("/crm/demo-config", patch, scoped()));
}

// --- Errores ----------------------------------------------------------------

type ApiError = { response?: { status?: number; data?: { meta?: { code?: unknown; message?: unknown } } }; code?: string; name?: string };

/** Código estable de la API (`meta.code`), si la respuesta lo trae. */
export function demoErrorCode(error: unknown): string | null {
  const code = (error as ApiError)?.response?.data?.meta?.code;
  return typeof code === "string" ? code : null;
}

/** Mensaje ya traducido por la API, o uno genérico. */
export function demoErrorMessage(error: unknown, fallback = "No se pudo completar la operación."): string {
  const message = (error as ApiError)?.response?.data?.meta?.message;
  return typeof message === "string" && message.trim() ? message : fallback;
}

export function isCanceled(error: unknown): boolean {
  const e = error as ApiError;
  return e?.code === "ERR_CANCELED" || e?.name === "CanceledError" || e?.name === "AbortError";
}

// --- Estado para mostrar ----------------------------------------------------
// Lógica pura: qué decir y qué botones ofrecer según el puntero y la cuenta.

export type DemoStatusKey =
  | "none"
  | "requested"
  | "invited"
  | "active"
  | "converted"
  | "closed"
  | "purged"
  | "needs_email"
  | "error";

export interface DemoStatus {
  key: DemoStatusKey;
  label: string;
  /** Clases del distintivo. */
  tone: string;
}

const TONES: Record<DemoStatusKey, string> = {
  none: "bg-gray-400/15 text-gray-500",
  requested: "bg-sky-500/15 text-sky-600",
  invited: "bg-amber-500/15 text-amber-600",
  active: "bg-emerald-500/15 text-emerald-600",
  converted: "bg-indigo-500/15 text-indigo-500",
  closed: "bg-orange-500/15 text-orange-600",
  purged: "bg-gray-400/15 text-gray-500",
  needs_email: "bg-amber-500/15 text-amber-600",
  error: "bg-red-500/15 text-red-500",
};

/** Fecha corta en español, o «—». Acepta ISO o el timestamp serializado de Firestore. */
export function formatDemoDate(value: unknown, withTime = false): string {
  if (!value) return "—";
  let date: Date | null = null;
  if (typeof value === "string" || typeof value === "number") date = new Date(value);
  else if (typeof value === "object") {
    const seconds = (value as { _seconds?: number; seconds?: number })._seconds ?? (value as { seconds?: number }).seconds;
    if (typeof seconds === "number") date = new Date(seconds * 1000);
  }
  if (!date || Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("es-MX", {
    day: "numeric",
    month: "short",
    year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
}

/**
 * El estado que ve el vendedor. Manda la CUENTA (es la verdad, leída en vivo);
 * el puntero sólo habla cuando todavía no hay cuenta que leer.
 */
export function demoStatusOf(demo: DealDemoPointer | null | undefined, account: DemoAccount | null | undefined): DemoStatus {
  const make = (key: DemoStatusKey, label: string): DemoStatus => ({ key, label, tone: TONES[key] });

  if (account) {
    switch (account.status) {
      case "invited":
        return make("invited", "Invitada, aún no entra");
      case "active":
        return make("active", "Ya entró");
      case "converted":
        return make("converted", "Convertida");
      case "closed":
        return make(
          "closed",
          account.purgeAfter ? `Cerrada, se borra el ${formatDemoDate(account.purgeAfter)}` : "Cerrada",
        );
      case "purged":
        return make("purged", "Borrada");
      default:
        break;
    }
  }

  if (!demo || isInternalMarker(demo)) return make("none", "Sin demo");
  // El tope por hora frenó la operación: no falló nada, está en fila.
  if (demo.state === "error" && demo.error?.code === "DEMO_RATE_CAPPED") return make("requested", "En fila (tope por hora)");
  switch (demo.state) {
    case "needs_email":
      return make("needs_email", "Falta correo");
    case "error":
      return make("error", "Error");
    case "converted":
      return make("converted", "Convertida");
    case "closed":
      return make("closed", "Cerrada");
    case "purged":
      return make("purged", "Borrada");
    case "requested":
    case "ready":
    default:
      // Hay puntero pero la cuenta no se pudo leer: se sabe que se pidió.
      return make("requested", "Solicitada");
  }
}

/**
 * Anotación interna del automatismo («no se pudo leer la etapa en HubSpot, se
 * reintenta»): todavía no hay demo, así que no se muestra como una.
 */
export function isInternalMarker(demo: DealDemoPointer | null | undefined): boolean {
  return Boolean(demo && !demo.accountId && demo.error?.code === "DEMO_PHASE_UNKNOWN");
}

/**
 * «Se cerrará en ~10 min» / «Se convertirá en ~3 min». Es aproximado a
 * propósito: la revisión corre cada 5 minutos.
 */
export function pendingLabel(pending: DemoPending | null | undefined, nowMs: number = Date.now()): string | null {
  if (!pending) return null;
  const verb = pending.action === "convert" ? "Se convertirá" : "Se cerrará";
  const due = pending.dueAt ? Date.parse(pending.dueAt) : NaN;
  if (Number.isNaN(due)) return `${verb} en unos minutos`;
  const minutes = Math.ceil((due - nowMs) / 60000);
  return minutes <= 0 ? `${verb} en la próxima revisión` : `${verb} en ~${minutes} min`;
}

/**
 * Distintivo de la tarjeta del tablero, o null si no toca mostrar ninguno. Ahí
 * sólo se tiene el puntero (la cuenta no se lee por cada tarjeta), así que dice
 * lo que el automatismo sabe: «creada» es que la plataforma confirmó la
 * cuenta, no que alguien ya haya entrado. Lo pendiente manda: es lo que va a pasar.
 */
export function demoBadgeOf(demo: DealDemoPointer | null | undefined, nowMs: number = Date.now()): DemoStatus | null {
  if (!demo || isInternalMarker(demo)) return null;
  const pending = pendingLabel(demo.pending, nowMs);
  if (pending) return { key: "closed", label: pending, tone: TONES.closed };
  if (demo.state === "ready") return { key: "active", label: "Creada", tone: TONES.active };
  return demoStatusOf(demo, null);
}

export interface DemoActions {
  create: boolean;
  invite: boolean;
  convert: boolean;
  close: boolean;
  reopen: boolean;
  purge: boolean;
  /** Reintentar la carga de datos de ejemplo (falló o se quedó atorada). */
  reseed: boolean;
  /** Cancelar el cierre/conversión programado. */
  cancelPending: boolean;
}

/**
 * Qué acciones tienen sentido, según las reglas de estado del contrato con la
 * plataforma. Es comodidad: quien decide de verdad es la API de allá.
 */
export function demoActionsOf(
  demo: DealDemoPointer | null | undefined,
  account: DemoAccount | null | undefined,
  accountError?: string | null,
): DemoActions {
  const none: DemoActions = {
    create: false,
    invite: false,
    convert: false,
    close: false,
    reopen: false,
    purge: false,
    reseed: false,
    cancelPending: Boolean(demo?.pending),
  };
  if (account) {
    switch (account.status) {
      case "invited":
        return { ...none, invite: true, convert: true, close: true, purge: true };
      case "active":
        // Ya reclamó la invitación: un enlace nuevo no le sirve a nadie (y la
        // plataforma lo rechaza). Los datos de ejemplo se reintentan si su
        // carga falló o sigue en curso; si no aplica, la plataforma lo dice.
        return {
          ...none,
          convert: true,
          close: true,
          purge: true,
          reseed: ["error", "queued", "seeding"].includes(account.seed?.status ?? ""),
        };
      case "closed":
        return { ...none, convert: true, reopen: true, purge: true };
      default:
        // Convertida o borrada: por este canal ya no se toca.
        return none;
    }
  }
  // Sin cuenta que leer. Si el puntero no tiene id de cuenta, la demo no
  // existe (no se creó, faltó el correo, falló el alta o la frenó el tope): se
  // puede crear. Si lo tiene, existe y simplemente no respondió la plataforma.
  if (!demo || !demo.accountId) return { ...none, create: true };
  // La plataforma contestó que esa cuenta NO existe (el alta se quedó a medias):
  // crearla es seguro, el id es el mismo y no puede duplicarse.
  if (accountError === "DEMO_ACCOUNT_NOT_FOUND") return { ...none, create: true };
  return none;
}

/** Segundos → «45 s», «3 min», «1 h 20 min». */
export function formatDuration(seconds: number | null | undefined): string {
  const s = Math.round(Number(seconds) || 0);
  if (s <= 0) return "0 s";
  if (s < 60) return `${s} s`;
  const minutes = Math.round(s / 60);
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

const SEED_LABELS: Record<string, string> = {
  pending: "Pendientes (se cargan cuando entra)",
  queued: "En cola",
  seeding: "Cargando…",
  done: "Cargados",
  removed: "Borrados",
  error: "Falló la carga",
};

/** Estado de los datos de ejemplo, con el total de registros cuando ya se cargaron. */
export function seedLabel(seed: DemoAccount["seed"]): string {
  if (!seed || !seed.status) return "—";
  const label = SEED_LABELS[seed.status] ?? seed.status;
  const total = Object.values(seed.counts ?? {}).reduce((acc, n) => acc + (Number(n) || 0), 0);
  return seed.status === "done" && total > 0 ? `${label} (${total} registros)` : label;
}
